import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Handle,
  Position,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Zap, Database, GitBranch, Send, UserCircle, Tag,
  Play, Save, ArrowLeft, Trash2, Loader2,
} from 'lucide-react';

// ─── Node visuals ───
const NODE_META = {
  trigger:         { icon: Zap,        color: 'border-amber-400 bg-amber-50',     label: 'Trigger' },
  fetch_context:   { icon: Database,   color: 'border-blue-400 bg-blue-50',       label: 'Fetch Context' },
  if_else:         { icon: GitBranch,  color: 'border-purple-400 bg-purple-50',   label: 'If / Else' },
  send_template:   { icon: Send,       color: 'border-emerald-400 bg-emerald-50', label: 'Send Template' },
  smart_handoff:   { icon: UserCircle, color: 'border-cyan-400 bg-cyan-50',       label: 'Smart Handoff' },
  update_crm_stage:{ icon: Tag,        color: 'border-pink-400 bg-pink-50',       label: 'Update CRM Stage' },
};

function GenericNode({ data, type, selected }) {
  const meta = NODE_META[type] || NODE_META.trigger;
  const Icon = meta.icon;
  const summary = (() => {
    if (type === 'trigger') return data.trigger_type || 'flow_complete';
    if (type === 'fetch_context') return data.source || 'aniyor';
    if (type === 'if_else') return data.condition || '— set condition';
    if (type === 'send_template') return data.template_name || '— pick template';
    if (type === 'smart_handoff') return data.agent_id ? `agent #${data.agent_id}` : 'use vendor default';
    if (type === 'update_crm_stage') return data.stage || '— set stage';
    return '';
  })();

  return (
    <div
      data-testid={`flow-node-${type}`}
      className={`min-w-[180px] rounded-lg border-2 ${meta.color} shadow-sm ${selected ? 'ring-2 ring-[#00D1B2]' : ''}`}
    >
      {type !== 'trigger' && <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2 !h-2" />}
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
          <Icon className="w-3.5 h-3.5" /> {meta.label}
        </div>
        <p className="text-xs text-slate-700 mt-1 truncate font-mono">{summary}</p>
      </div>
      {type === 'if_else' ? (
        <>
          <Handle type="source" position={Position.Right} id="true" style={{ top: '35%', background: '#10b981' }} className="!w-2.5 !h-2.5" />
          <Handle type="source" position={Position.Right} id="false" style={{ top: '70%', background: '#ef4444' }} className="!w-2.5 !h-2.5" />
        </>
      ) : (
        <Handle type="source" position={Position.Right} className="!bg-slate-400 !w-2 !h-2" />
      )}
    </div>
  );
}

const NODE_TYPES = Object.fromEntries(
  Object.keys(NODE_META).map((t) => [t, (props) => <GenericNode {...props} type={t} />])
);

// ─── Right-hand inspector ───
function NodeInspector({ node, onChange, templates }) {
  if (!node) {
    return (
      <div className="p-5 text-xs text-slate-400">
        Select a node to edit its properties, or drag a node from the left palette onto the canvas.
      </div>
    );
  }
  const data = node.data || {};
  const update = (patch) => onChange({ ...node, data: { ...data, ...patch } });

  return (
    <div className="p-5 space-y-4" data-testid={`inspector-${node.type}`}>
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
        {NODE_META[node.type]?.label || node.type}
      </h3>

      {node.type === 'trigger' && (
        <div>
          <Label>Trigger event</Label>
          <Select value={data.trigger_type || 'flow_complete'} onValueChange={(v) => update({ trigger_type: v })}>
            <SelectTrigger data-testid="trigger-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="flow_complete">WhatsApp Flow completed</SelectItem>
              <SelectItem value="message_received">Customer message received</SelectItem>
              <SelectItem value="lead_stage">Lead stage changed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {node.type === 'fetch_context' && (
        <div>
          <Label>Source</Label>
          <Select value={data.source || 'aniyor'} onValueChange={(v) => update({ source: v })}>
            <SelectTrigger data-testid="fetch-source-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aniyor">Aniyor (product + vendor)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-400 mt-2">
            Pulls product/vendor/page_info from Aniyor and exposes them as <code>context.*</code>.
          </p>
        </div>
      )}

      {node.type === 'if_else' && (
        <div>
          <Label>Condition</Label>
          <Textarea
            data-testid="ifelse-condition-input"
            rows={2}
            value={data.condition || ''}
            onChange={(e) => update({ condition: e.target.value })}
            placeholder="context.product.category == 'puja_items'"
          />
          <p className="text-[10px] text-slate-400 mt-2">
            Operators: == != &gt; &lt; &gt;= &lt;= in exists. Strings in single quotes.
          </p>
        </div>
      )}

      {node.type === 'send_template' && (
        <>
          <div>
            <Label>Approved template</Label>
            {templates.length > 0 ? (
              <Select value={data.template_name || ''} onValueChange={(v) => update({ template_name: v })}>
                <SelectTrigger data-testid="send-template-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                data-testid="send-template-input"
                value={data.template_name || ''}
                onChange={(e) => update({ template_name: e.target.value })}
                placeholder="hello_world"
              />
            )}
          </div>
          <div>
            <Label>Language</Label>
            <Input value={data.language || 'en_US'} onChange={(e) => update({ language: e.target.value })} />
          </div>
        </>
      )}

      {node.type === 'smart_handoff' && (
        <div>
          <Label>Agent ID (leave blank to use vendor default)</Label>
          <Input
            data-testid="handoff-agent-input"
            type="number"
            value={data.agent_id || ''}
            onChange={(e) => update({ agent_id: e.target.value ? parseInt(e.target.value, 10) : null })}
            placeholder="e.g. 7"
          />
        </div>
      )}

      {node.type === 'update_crm_stage' && (
        <div>
          <Label>Set lead_stage to</Label>
          <Select value={data.stage || ''} onValueChange={(v) => update({ stage: v })}>
            <SelectTrigger data-testid="crm-stage-select"><SelectValue placeholder="Pick a stage" /></SelectTrigger>
            <SelectContent>
              {['Prospect', 'Aware', 'Engaged', 'Qualified', 'Won', 'Lost'].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// ─── Left-hand palette ───
function NodePalette({ onAdd }) {
  return (
    <div className="p-4 space-y-2" data-testid="node-palette">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-3">Drag or click to add</h3>
      {Object.entries(NODE_META)
        .filter(([t]) => t !== 'trigger') // trigger is seeded
        .map(([type, meta]) => {
          const Icon = meta.icon;
          return (
            <button
              key={type}
              data-testid={`palette-${type}`}
              onClick={() => onAdd(type)}
              className={`w-full text-left p-2.5 rounded-md border-2 ${meta.color} hover:shadow-sm transition`}
            >
              <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                <Icon className="w-3.5 h-3.5" /> {meta.label}
              </div>
            </button>
          );
        })}
    </div>
  );
}

// ─── Test-run panel ───
function TestRunPanel({ graphId, onClose }) {
  const [phone, setPhone] = useState('+919999990001');
  const [productId, setProductId] = useState('puja_001');
  const [running, setRunning] = useState(false);
  const [execution, setExecution] = useState(null);

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.post(`/api/flow-graphs/${graphId}/test-run`, {
        initial_ctx: { phone, product_id: productId, customer_phone: phone },
      });
      setExecution(res.execution);
      toast.success('Dry-run complete');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="absolute right-4 bottom-4 w-96 max-h-[60vh] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden flex flex-col z-10" data-testid="test-run-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Dry run</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
      </div>
      <div className="p-4 space-y-3 overflow-y-auto">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px]">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px]">Product ID</Label>
            <Input value={productId} onChange={(e) => setProductId(e.target.value)} />
          </div>
        </div>
        <Button onClick={run} disabled={running} className="w-full bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2" data-testid="dryrun-run-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Run dry
        </Button>
        {execution && (
          <div className="space-y-1 text-xs" data-testid="dryrun-timeline">
            <p className="font-semibold text-slate-700">Timeline</p>
            {execution.timeline.map((t, i) => (
              <div key={i} className={`p-2 rounded ${t.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                <span className="font-mono">{i + 1}. {t.type}</span>
                <span className="text-[10px] ml-2 opacity-70">{t.ms}ms</span>
                <pre className="text-[10px] mt-1 whitespace-pre-wrap break-words opacity-80">
                  {JSON.stringify(t.log, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Editor (provider wrapper) ───
function FlowEditorInner() {
  const { graphId } = useParams();
  const navigate = useNavigate();
  const [graph, setGraph] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTestRun, setShowTestRun] = useState(false);
  const [templates, setTemplates] = useState([]);
  const dirtyRef = useRef(false);

  useEffect(() => {
    Promise.all([
      api.get(`/api/flow-graphs/${graphId}`),
      api.get('/api/templates/approved').catch(() => ({ templates: [] })),
    ]).then(([g, t]) => {
      setGraph(g);
      // React Flow requires { position: {x,y} } on every node. Synthesize a
      // grid layout for legacy/imported graphs that lack it.
      const rawNodes = g.graph?.nodes || [];
      const normalised = rawNodes.map((n, i) => ({
        ...n,
        position: n.position || { x: 100 + (i % 4) * 240, y: 80 + Math.floor(i / 4) * 160 },
      }));
      setNodes(normalised);
      setEdges(g.graph?.edges || []);
      setTemplates(t.templates || []);
    }).catch((e) => {
      toast.error(e.message);
    });
  }, [graphId]);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
    dirtyRef.current = true;
  }, []);
  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    dirtyRef.current = true;
  }, []);
  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, id: `e${Date.now()}` }, eds));
    dirtyRef.current = true;
  }, []);
  const onNodeClick = useCallback((_e, node) => {
    setSelectedNodeId(node.id);
  }, []);

  const addNode = (type) => {
    const id = `${type}-${Date.now()}`;
    const position = { x: 350 + Math.random() * 200, y: 100 + Math.random() * 300 };
    const data = type === 'send_template' ? { language: 'en_US' } : {};
    setNodes((nds) => [...nds, { id, type, position, data }]);
    dirtyRef.current = true;
  };

  const updateNode = (next) => {
    setNodes((nds) => nds.map((n) => (n.id === next.id ? { ...n, data: next.data } : n)));
    dirtyRef.current = true;
  };

  const removeSelected = () => {
    if (!selectedNodeId) return;
    const sel = nodes.find((n) => n.id === selectedNodeId);
    if (sel?.type === 'trigger') {
      toast.error('Cannot delete the trigger node');
      return;
    }
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
    dirtyRef.current = true;
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/api/flow-graphs/${graphId}`, {
        graph: { nodes, edges },
      });
      setGraph(res.graph);
      dirtyRef.current = false;
      toast.success(`Saved · v${res.graph.version}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const next = graph.status === 'active' ? 'draft' : 'active';
    try {
      const res = await api.put(`/api/flow-graphs/${graphId}`, { status: next });
      setGraph(res.graph);
      toast.success(next === 'active' ? 'Activated' : 'Paused');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  if (!graph) return <div className="p-12 text-sm text-slate-400">Loading editor…</div>;

  return (
    <div className="h-[calc(100vh-180px)] flex bg-slate-50 rounded-xl overflow-hidden border border-gray-200" data-testid="flow-editor">
      {/* Palette */}
      <div className="w-56 bg-white border-r border-gray-100 overflow-y-auto">
        <NodePalette onAdd={addNode} />
      </div>

      {/* Canvas */}
      <div className="flex-1 relative">
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-gray-100">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/flow-builder')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <p className="text-sm font-semibold text-slate-900">{graph.name}</p>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${graph.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            {graph.status} · v{graph.version}
          </span>
        </div>

        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="dryrun-toggle-btn"
            onClick={() => setShowTestRun((v) => !v)}
            className="gap-2"
          >
            <Play className="w-3.5 h-3.5" /> Dry run
          </Button>
          <Button
            data-testid="save-graph-btn"
            size="sm"
            onClick={save}
            disabled={saving}
            className="bg-[#00D1B2] hover:bg-[#00B89C] text-white gap-2"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            data-testid="activate-graph-btn"
            size="sm"
            variant={graph.status === 'active' ? 'outline' : 'default'}
            onClick={toggleActive}
          >
            {graph.status === 'active' ? 'Pause' : 'Activate'}
          </Button>
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={NODE_TYPES}
          fitView
          deleteKeyCode={null}
        >
          <Background gap={16} color="#e2e8f0" />
          <Controls />
          <MiniMap pannable zoomable />
        </ReactFlow>

        {showTestRun && <TestRunPanel graphId={graphId} onClose={() => setShowTestRun(false)} />}
      </div>

      {/* Inspector */}
      <div className="w-80 bg-white border-l border-gray-100 overflow-y-auto" data-testid="inspector-panel">
        {selectedNode && (
          <div className="px-5 pt-4 flex items-center justify-between">
            <span className="text-[10px] font-mono text-slate-400">{selectedNode.id}</span>
            {selectedNode.type !== 'trigger' && (
              <Button variant="ghost" size="icon" onClick={removeSelected} data-testid="delete-node-btn">
                <Trash2 className="w-4 h-4 text-rose-500" />
              </Button>
            )}
          </div>
        )}
        <NodeInspector node={selectedNode} onChange={updateNode} templates={templates} />
      </div>
    </div>
  );
}

export function FlowEditorPage() {
  return (
    <ReactFlowProvider>
      <FlowEditorInner />
    </ReactFlowProvider>
  );
}
